import Image from "next/image";
import { useTrans } from "@/utils/useTrans";
import ComponentErrorBoundary from "@/v2-components/pages/ComponentErrorBoundary";

const STEPS = [
  {
    step: "Step 1",
    titleKey: "live_steps_1_title",
    bodyKey: "live_steps_1_body",
    img: "/images/software/live/step1.jpg",
  },
  {
    step: "Step 2",
    titleKey: "live_steps_2_title",
    bodyKey: "live_steps_2_body",
    img: "/images/software/live/step2.jpg",
  },
  {
    step: "Step 3",
    titleKey: "live_steps_3_title",
    bodyKey: "live_steps_3_body",
    img: "/images/software/live/step3.jpg",
  },
] as const;

type StepData = { step: string; title: string; body: string; img: string };

function StepCardDesktop({ step, title, body, img }: StepData) {
  return (
    <article className="flex min-w-0 flex-1 flex-col gap-[20px]">
      <div className="flex h-[48px] w-fit items-center justify-center rounded-full bg-blue-10 px-[24px]">
        <span className="whitespace-nowrap text-xl font-600 leading-[24px] tracking-[0.1px] text-blue-60">
          {step}
        </span>
      </div>
      <div className="relative h-[320px] w-full overflow-hidden rounded-[16px]">
        <Image src={img} alt={title} fill className="object-cover" />
      </div>
      <div className="flex w-full flex-col gap-[12px]">
        <h3 className="text-2xl font-400 leading-[28px] text-coolgray-90">
          {title}
        </h3>
        <p className="whitespace-pre-line text-base font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
          {body}
        </p>
      </div>
    </article>
  );
}

function StepCardMobile({ step, title, body, img }: StepData) {
  return (
    <article className="flex w-full flex-col items-center gap-[20px]">
      <div className="flex h-[48px] w-fit items-center justify-center rounded-full bg-blue-10 px-[24px]">
        <span className="whitespace-nowrap text-lg font-600 leading-[24px] tracking-[0.1px] text-blue-60">
          {step}
        </span>
      </div>
      <div className="relative h-[230px] w-full overflow-hidden rounded-[16px]">
        <Image src={img} alt={title} fill className="object-cover" />
      </div>
      <div className="flex w-full flex-col items-center gap-[12px] text-center">
        <h3 className="text-[22px] font-400 leading-[28px] text-coolgray-90">
          {title}
        </h3>
        <p className="whitespace-pre-line text-sm font-400 leading-[18px] tracking-[0.25px] text-coolgray-90">
          {body}
        </p>
      </div>
    </article>
  );
}

export default function LiveStepsSection() {
  const { t } = useTrans(["software"]);
  const steps = STEPS.map((s) => ({
    step: s.step,
    title: t(s.titleKey),
    body: t(s.bodyKey),
    img: s.img,
  }));

  return (
    <ComponentErrorBoundary>
      <section id="how-it-works" className="w-full px-4">
        <div className="hidden md:block">
          <div className="mx-auto max-w-[1440px]">
            <h2 className="mb-[32px] text-5xl font-400 leading-[40px] text-coolgray-90">
              {t("live_steps_heading")}
            </h2>
            <div className="flex flex-row gap-[24px]">
              {steps.map((item) => (
                <StepCardDesktop key={item.step} {...item} />
              ))}
            </div>
          </div>
        </div>
        <div className="md:hidden">
          <div className="w-full">
            <h2 className="mb-[24px] text-center text-2xl font-400 leading-8 text-coolgray-90">
              {t("live_steps_heading")}
            </h2>
            <div className="flex flex-col gap-[64px]">
              {steps.map((item) => (
                <StepCardMobile key={item.step} {...item} />
              ))}
            </div>
          </div>
        </div>
      </section>
    </ComponentErrorBoundary>
  );
}
